/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faFloppyDisk, faForwardStep, faMinus, faPlus, faRotateLeft } from "@fortawesome/pro-regular-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import { Dayjs } from "dayjs";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, FormGroup, Input, InputGroup, InputGroupText, Label, Modal, ModalBody, ModalFooter, ModalHeader, Progress, Table } from "reactstrap";

import { PHASE_SLOT_NAMES, phaseHeading, phaseSlotName } from "../../../../constants/ripeningPhaseSlots";
import { formatProgramFieldValue, injectsN2, PROGRAM_FIELDS } from "../../../../constants/ripeningProgramFields";
import {
    RipeningCycleProgramEdit,
    useEditRipeningCycleProgram,
    useResumeRipeningCycleProgramEdit,
} from "../../../../hooks/useFacilityLocationQueries";
import { useRipeningRoomRun } from "../../../../hooks/useRipeningPrograms";
import { dayjs } from "../../../../utils/date";
import { toast } from "../../../../utils/toast";
import { Button } from "../../../Helper/Button";
import { getRunPhaseProgress, isRunning } from "./roomRun";
import RunningStoreEditor from "./RunningStoreEditor";
import {
    buildProgramEditPayload,
    buildProgramSchedule,
    describeDraft,
    describeGeneralDraft,
    EDITABLE_GENERAL_KEYS,
    EditableGeneralKey,
    formatDateTime,
    GeneralDraft,
    generalValue,
    getCycleProgram,
    getCycleStore,
    getLivePhaseName,
    getLivePhaseNumber,
    getPhaseSwitchAvailability,
    getProgramDisplayName,
    getSkipAvailability,
    getSkipConfirmation,
    MAX_TEMPERATURE,
    MIN_TEMPERATURE,
    nextRunningPhase,
    parseCycleDate,
    PhaseDraft,
    ProgramDraft,
    ScheduledPhase,
    sortedPhases,
    updateDraft,
    updateGeneralDraft,
    validateDraft,
    validateGeneralDraft,
} from "./runningProgram";

interface RunningProgramEditorProps {
    locationId: string;
    cycle: any;
    gatewayId?: string | null;
    roomId?: string | null;
    tenantId?: string | null;
    // * Called with the saved result ({ ripening_end_date, ... }) after a change was sent.
    onChanged?: (result: { ripening_end_date?: string } | undefined) => void;
    // * For tests: a fixed clock.
    now?: Dayjs;
}

const activeRowCss = css`
    td { font-weight: 600; background-color: rgba(13, 110, 253, 0.06); }
    /* The accent line sits inside the cell: keep the text clear of it. */
    td:first-of-type { box-shadow: inset 4px 0 0 var(--bs-primary, #0d6efd); padding-left: calc(0.5rem + 4px); }
`;
// * Room around text and inputs: nothing sits against a cell edge, neighbouring inputs keep a gap.
const programTableCss = css`
    && > :not(caption) > * > * { padding: 0.35rem 0.5rem; }
`;
const doneRowCss = css`
    td { color: var(--bs-secondary-color, #6c757d); background-color: var(--bs-tertiary-bg, #f8f9fa); }
`;
const rowCss = { active: activeRowCss, done: doneRowCss, future: undefined };
// * A phase that does not run: greyed out, still editable until it starts.
const offRowCss = css`
    td { color: var(--bs-secondary-color, #6c757d); background-color: var(--bs-tertiary-bg, #f8f9fa); }
`;
const smallInputCss = css`flex: 0 0 5.5rem !important; width: 5.5rem;`;
// * Input groups keep their own size instead of stretching across the column.
const compactGroupCss = css`width: auto; display: inline-flex;`;
const durationCss = css`min-width: 3.5rem; justify-content: center;`;
const startedCss = css`font-size: 1.15rem;`;

const formatTime = (value: Dayjs) => formatDateTime(value);

// * Same names as the program creator and preview; ripener screens show labels only.
const DURATION = PROGRAM_FIELDS.TimeSpan;
const END_TEMPERATURE = PROGRAM_FIELDS.T_End;
const ETHYLENE_DOSING = PROGRAM_FIELDS.Modus_C2H4;
const ETHYLENE_DOSAGE = PROGRAM_FIELDS.Spv_C2H4;
const onOffLabel = (active: boolean) => formatProgramFieldValue(ETHYLENE_DOSING.key, active ? 1 : 0);

/** The program of a running cycle: where it is now, and the few changes allowed mid-cycle. */
export default function RunningProgramEditor({ locationId, cycle, gatewayId, roomId, tenantId, onChanged, now: fixedNow }: RunningProgramEditorProps) {
    const program = useMemo(() => getCycleProgram(cycle?.meta), [cycle?.meta]);
    const startAt = useMemo(() => parseCycleDate(cycle?.ripening_start_date), [cycle?.ripening_start_date]);
    const queryClient = useQueryClient();
    const editMutation = useEditRipeningCycleProgram();
    const resumeEditMutation = useResumeRipeningCycleProgramEdit();
    const resumedCommandRef = useRef<string | null>(null);
    // * Silent: a missing room status only hides the live parts.
    const { data: roomStatus, isError } = useRipeningRoomRun(gatewayId, roomId, { tenantId });
    const run = !isError && isRunning(roomStatus?.run) ? roomStatus?.run : null;

    const [draft, setDraft] = useState<ProgramDraft>({});
    const [generalDraft, setGeneralDraft] = useState<GeneralDraft>({});
    const [confirmSkip, setConfirmSkip] = useState(false);
    const [clock, setClock] = useState(() => dayjs());
    useEffect(() => {
        if (fixedNow) return undefined;
        const timer = setInterval(() => setClock(dayjs()), 60000);
        return () => clearInterval(timer);
    }, [fixedNow]);
    useEffect(() => {
        setDraft({});
        setGeneralDraft({});
    }, [cycle?.id]);
    const pendingEditCommand = cycle?.meta?.controller_program_edit_command;
    useEffect(() => {
        const commandId = pendingEditCommand?.status === "pending" ? pendingEditCommand.command_id : null;
        if (!commandId || resumedCommandRef.current === commandId) return;
        resumedCommandRef.current = commandId;
        resumeEditMutation.mutate(
            { locationId, cycleId: cycle.id, commandId },
            {
                onSuccess: (result) => {
                    toast.success("Program changes accepted by the ripening room");
                    if (result?.warning) toast.warning(result.warning, { autoClose: false });
                    queryClient.invalidateQueries({ queryKey: ["ripeningRoomRun"], exact: false });
                    onChanged?.(result);
                },
                onError: () => onChanged?.(undefined),
            },
        );
    }, [locationId, cycle?.id, pendingEditCommand?.command_id, pendingEditCommand?.status]);
    const now = fixedNow ?? clock;

    const send = (payload: RipeningCycleProgramEdit, successMessage: string, onDone?: () => void) => {
        editMutation.mutate({ locationId, cycleId: cycle.id, payload }, {
            onSuccess: (result) => {
                toast.success(successMessage);
                // * The room took the change; an external system (SAP) rejecting the new end date is only a warning.
                if (result?.warning) toast.warning(result.warning, { autoClose: false });
                setDraft({});
                setGeneralDraft({});
                queryClient.invalidateQueries({ queryKey: ["ripeningRoomRun"], exact: false });
                onChanged?.(result);
                onDone?.();
            },
            onError: () => onChanged?.(undefined),
        });
    };
    if (!program || !startAt) return null;

    const store = getCycleStore(cycle?.meta);
    if (store) {
        return (
            <RunningStoreEditor
                key={cycle?.id}
                program={program}
                store={store}
                startAt={startAt}
                send={send}
                saving={editMutation.isPending}
                changePending={pendingEditCommand?.status === "pending" || resumeEditMutation.isPending}
            />
        );
    }

    const phases = sortedPhases(program);
    const livePhase = getLivePhaseNumber(run, phases);
    // * Phase names are AVOS configuration: name the running phase by its number.
    const liveName = livePhase !== null ? (PHASE_SLOT_NAMES[livePhase - 1] ?? getLivePhaseName(run)) : null;
    const before = buildProgramSchedule(program, startAt, now, livePhase);
    const schedule = buildProgramSchedule(program, startAt, now, livePhase, draft);
    const hasChanges = Object.keys(draft).length > 0 || Object.keys(generalDraft).length > 0;
    const errors = [...validateDraft(draft, before), ...validateGeneralDraft(generalDraft)];
    const summary = [...describeDraft(draft, program, before, schedule), ...describeGeneralDraft(generalDraft, program)];
    const programChangePending = pendingEditCommand?.status === "pending" || resumeEditMutation.isPending;
    // * How far the room says its current phase is: a skip records the whole hours it ran.
    const phaseProgress = livePhase !== null ? getRunPhaseProgress(run) : null;
    const skip = programChangePending
        ? { enabled: false, reason: "A program change is still being confirmed." }
        : getSkipAvailability(before, !!run, hasChanges, phaseProgress);
    const progress = before.progress;

    const setValue = (phase: number, key: keyof PhaseDraft, value: number | boolean | undefined) => {
        setDraft((prev) => updateDraft(prev, program, phase, key, value));
    };
    const numberOrUndefined = (value: string) => (value.trim() === "" || !Number.isFinite(Number(value)) ? undefined : Number(value));

    const setGeneral = (key: EditableGeneralKey, value: number | undefined) => {
        setGeneralDraft((prev) => updateGeneralDraft(prev, program, key, value));
    };
    const undoChanges = () => {
        setDraft({});
        setGeneralDraft({});
    };

    const onSave = () => send(buildProgramEditPayload(draft, generalDraft), "Program changes sent to the ripening room");
    const onSkip = () => send({ phases: [], proceed_to_next_phase: true }, "Next phase started", () => setConfirmSkip(false));

    const renderEditableCells = (phase: ScheduledPhase) => {
        const original = phases.find((item) => item.phase === phase.phase);
        const minDuration = phase.status === "active" ? Math.floor(before.elapsedInCurrentHours) + 1 : 1;
        const duration = phase.durationHours;
        return <>
            <td>
                <InputGroup size="sm" className="flex-nowrap" css={compactGroupCss}>
                    <Button size="sm" color="light" icon={faMinus} aria-label={`Shorter phase ${phase.phase}`} disabled={duration <= minDuration} onClick={() => setValue(phase.phase, "duration_hours", duration - 1)} />
                    <InputGroupText data-testid={`duration-${phase.phase}`} css={durationCss}>{duration} h</InputGroupText>
                    <Button size="sm" color="light" icon={faPlus} aria-label={`Longer phase ${phase.phase}`} onClick={() => setValue(phase.phase, "duration_hours", duration + 1)} />
                </InputGroup>
            </td>
            <td>
                <Label className="visually-hidden" for={`t-end-${phase.phase}`}>{END_TEMPERATURE.label} phase {phase.phase}</Label>
                <InputGroup size="sm" className="flex-nowrap" css={compactGroupCss}>
                    <Input
                        id={`t-end-${phase.phase}`}
                        type="number"
                        step="0.5"
                        min={MIN_TEMPERATURE}
                        max={MAX_TEMPERATURE}
                        css={smallInputCss}
                        value={draft[phase.phase]?.t_end ?? original?.values.T_End ?? ""}
                        onChange={(event) => setValue(phase.phase, "t_end", numberOrUndefined(event.target.value))}
                    />
                    <InputGroupText>{END_TEMPERATURE.unit}</InputGroupText>
                </InputGroup>
            </td>
            <td>
                <div className="d-flex align-items-center gap-2 flex-nowrap">
                    <Label className="visually-hidden" for={`ethylene-${phase.phase}`}>{ETHYLENE_DOSAGE.label} phase {phase.phase}</Label>
                    <InputGroup size="sm" className="flex-nowrap" css={compactGroupCss}>
                        <Input
                            id={`ethylene-${phase.phase}`}
                            type="number"
                            min={0}
                            step="any"
                            css={smallInputCss}
                            value={draft[phase.phase]?.ethylene_setpoint ?? original?.values.Spv_C2H4 ?? ""}
                            onChange={(event) => setValue(phase.phase, "ethylene_setpoint", numberOrUndefined(event.target.value))}
                        />
                        <InputGroupText>{ETHYLENE_DOSAGE.unit}</InputGroupText>
                    </InputGroup>
                    {/* * Dosing is not a switch: it follows the setpoint. */}
                    <span className="small text-muted text-nowrap" data-testid={`ethylene-state-${phase.phase}`} title="Dosing is on when the dosage is above 0">
                        {onOffLabel(phase.ethyleneActive)}
                    </span>
                </div>
            </td>
        </>;
    };

    const renderPhaseSwitch = (phase: ScheduledPhase) => {
        const availability = getPhaseSwitchAvailability(phase);
        if (!availability.enabled) {
            return !phase.enabled ? <div className="small text-muted" data-testid={`phase-switch-reason-${phase.phase}`}>Not run · {availability.reason}</div> : null;
        }
        return (
            <FormGroup switch className="mb-0 mt-1 small">
                <Input
                    id={`phase-on-${phase.phase}`}
                    type="switch"
                    aria-label={`Run ${phaseSlotName(phase.phase)} phase ${phase.phase}`}
                    checked={phase.enabled}
                    disabled={editMutation.isPending || programChangePending}
                    onChange={(event) => setValue(phase.phase, "enabled", event.target.checked)}
                />
                <Label check for={`phase-on-${phase.phase}`} aria-hidden>{phase.enabled ? "On" : "Off"}</Label>
            </FormGroup>
        );
    };

    const renderReadOnlyCells = (phase: ScheduledPhase) => <>
        <td>{phase.durationHours} {DURATION.unit}</td>
        <td>{phase.tEnd === null ? "-" : `${phase.tEnd} ${END_TEMPERATURE.unit}`}</td>
        <td>{phase.ethyleneActive ? `${phase.ethyleneSetpoint} ${ETHYLENE_DOSAGE.unit}` : onOffLabel(false)}</td>
    </>;

    const nextPhase = nextRunningPhase(before);

    return (
        <div data-testid="running-program-editor">
            <h5 className="mb-1">Program</h5>
            <div className="fw-semibold mb-2">{getProgramDisplayName(program)}</div>
            <div className="d-flex flex-wrap gap-3 align-items-baseline mb-2">
                <div css={startedCss} data-testid="program-started">
                    <span className="text-muted">Started: </span>
                    <span className="fw-bold">{formatTime(startAt)}</span>
                </div>
                <div data-testid="program-ends">
                    <span className="text-muted">Ends: </span>
                    <span className="fw-bold">{formatTime(before.endAt)}</span>
                </div>
            </div>
            <div className="d-flex align-items-center gap-2 mb-3" aria-label={`Program progress: ${progress}%`}>
                <Progress value={progress} color="success" className="flex-grow-1" />
                <span className="fw-semibold text-success small">{progress}%</span>
            </div>

            <div className="table-responsive">
                <Table size="sm" className="align-middle mb-2" css={programTableCss}>
                    <thead>
                        <tr>
                            <th>Phase</th>
                            <th>Start – end</th>
                            <th>{DURATION.label}</th>
                            <th>{END_TEMPERATURE.label}</th>
                            <th>{ETHYLENE_DOSING.label}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {schedule.phases.map((phase) => (
                            <Fragment key={phase.phase}>
                                <tr
                                    data-testid={`program-phase-${phase.phase}`}
                                    data-status={phase.status}
                                    data-enabled={phase.enabled}
                                    css={phase.enabled ? rowCss[phase.status] : offRowCss}
                                    aria-current={phase.status === "active" ? "step" : undefined}
                                >
                                    <td className="text-nowrap">
                                        <span data-testid={`phase-name-${phase.phase}`}>{phaseHeading(phase.phase).title}</span>
                                        {phase.status === "active" && <Badge color="primary" className="ms-2" data-testid={livePhase !== null ? "live-phase" : undefined}>Now</Badge>}
                                        {phase.status === "done" && phase.enabled && <Badge color="secondary" className="ms-2">Done</Badge>}
                                        {phaseHeading(phase.phase).subtitle && (
                                            <div className="small text-muted" data-testid={`phase-number-${phase.phase}`}>{phaseHeading(phase.phase).subtitle}</div>
                                        )}
                                        {injectsN2(phases.find((item) => item.phase === phase.phase)?.values) && (
                                            <div
                                                className="small fw-semibold text-warning-emphasis"
                                                data-testid={`low-oxygen-${phase.phase}`}
                                                title="N2 injection: the room is dangerous to enter while this phase runs."
                                            >
                                                {"⚠ "}Low oxygen
                                            </div>
                                        )}
                                        {phase.status === "active" && liveName && liveName !== phaseSlotName(phase.phase) && (
                                            <div className="small text-muted" data-testid="active-phase-name">{liveName}</div>
                                        )}
                                        {renderPhaseSwitch(phase)}
                                    </td>
                                    <td className="text-nowrap small">
                                        {phase.enabled ? `${formatTime(phase.startAt)} – ${formatTime(phase.endAt)}` : <span className="text-muted">Not running</span>}
                                    </td>
                                    {phase.status === "done" ? renderReadOnlyCells(phase) : renderEditableCells(phase)}
                                </tr>
                                {phase.status === "active" && (
                                    <tr css={activeRowCss}>
                                        <td colSpan={5}>
                                            <div className="d-flex align-items-center justify-content-end gap-2">
                                                {skip.reason && <span className="small text-muted fw-normal" data-testid="skip-reason">{skip.reason}</span>}
                                                <Button
                                                    color="warning"
                                                    size="sm"
                                                    className="text-nowrap text-dark fw-semibold"
                                                    icon={faForwardStep}
                                                    disabled={!skip.enabled || editMutation.isPending}
                                                    onClick={() => setConfirmSkip(true)}
                                                >
                                                    Skip to next phase
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        ))}
                    </tbody>
                </Table>
            </div>

            <div className="mb-3" data-testid="program-ventilation">
                <h6 className="mb-2">Ventilation</h6>
                <div className="d-flex flex-wrap gap-3">
                    {EDITABLE_GENERAL_KEYS.map((key) => {
                        const fieldDefinition = PROGRAM_FIELDS[key];
                        return (
                            <FormGroup key={key} className="mb-0">
                                <Label for={`general-${key}`} className="small mb-1">{fieldDefinition.label}</Label>
                                <InputGroup size="sm" className="flex-nowrap" css={compactGroupCss}>
                                    <Input
                                        id={`general-${key}`}
                                        type="number"
                                        min={0}
                                        step={1}
                                        css={smallInputCss}
                                        value={generalDraft[key] ?? generalValue(program, key) ?? ""}
                                        disabled={editMutation.isPending || programChangePending}
                                        onChange={(event) => setGeneral(key, numberOrUndefined(event.target.value))}
                                    />
                                    <InputGroupText>{fieldDefinition.unit}</InputGroupText>
                                </InputGroup>
                            </FormGroup>
                        );
                    })}
                </div>
            </div>

            {hasChanges && (
                <Alert color={errors.length > 0 ? "danger" : "info"} className="mb-2" data-testid="program-change-summary">
                    {errors.length > 0 ? (
                        <ul className="mb-0 ps-3">{errors.map((error) => <li key={error}>{error}</li>)}</ul>
                    ) : (
                        <>
                            <div className="fw-semibold mb-1">This will change:</div>
                            <ul className="mb-0 ps-3">{summary.map((line) => <li key={line}>{line}</li>)}</ul>
                        </>
                    )}
                </Alert>
            )}
            <div className="d-flex gap-2 justify-content-end">
                {hasChanges && <Button color="light" icon={faRotateLeft} onClick={undoChanges} disabled={editMutation.isPending}>Undo changes</Button>}
                <Button
                    color="primary"
                    icon={faFloppyDisk}
                    onClick={onSave}
                    isLoading={editMutation.isPending && !confirmSkip}
                    disabled={!hasChanges || errors.length > 0 || editMutation.isPending || programChangePending}
                >
                    Save program changes
                </Button>
            </div>

            <Modal isOpen={confirmSkip} toggle={() => setConfirmSkip(false)} size="md">
                <ModalHeader toggle={() => setConfirmSkip(false)}>Skip to phase {nextPhase?.phase}?</ModalHeader>
                <ModalBody>
                    <p className="mb-0" data-testid="skip-confirmation">{getSkipConfirmation(before, liveName, phaseProgress)}</p>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setConfirmSkip(false)} disabled={editMutation.isPending}>Cancel</Button>
                    <Button color="warning" icon={faForwardStep} onClick={onSkip} isLoading={editMutation.isPending} disabled={editMutation.isPending}>Skip phase</Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}
